import { Grid, makeStyles, Paper, Typography, Button, Divider } from '@material-ui/core';
import { Link } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1200,
    margin: '0 auto',
  },
  welcomeCard: {
    padding: theme.spacing(4),
    marginBottom: theme.spacing(3),
    background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
    color: '#fff',
    borderRadius: 12,
  },
  welcomeTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  welcomeSubtitle: {
    opacity: 0.85,
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  shortcutGrid: {
    marginBottom: theme.spacing(3),
  },
  shortcutCard: {
    padding: theme.spacing(2.5),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 10,
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[6],
    },
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
  },
  shortcutIcon: {
    fontSize: 36,
    marginBottom: theme.spacing(1),
  },
  shortcutName: {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(0.5),
  },
  shortcutDesc: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    flex: 1,
  },
  noticeCard: {
    padding: theme.spacing(2.5),
    borderRadius: 10,
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.warning.main}`,
  },
  noticeTitle: {
    fontWeight: 600,
    fontSize: '0.95rem',
  },
  noticeBody: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  quickLinks: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  quickLinkBtn: {
    textTransform: 'none',
    borderRadius: 20,
    fontSize: '0.8rem',
  },
  shortcutLink: {
    textDecoration: 'none',
  },
}));

interface Shortcut {
  name: string;
  url: string;
  icon: string;
  description: string;
  color: string;
}

interface BigPictureProject {
  name: string;
  url: string;
  icon: string;
  description: string;
  color: string;
}

const shortcuts: Shortcut[] = [
  {
    name: 'Mattermost',
    url: 'https://mattermost.eigen.co.id',
    icon: '💬',
    description: 'Team communication & collaboration',
    color: '#2389D7',
  },
  {
    name: 'Git (Gitea)',
    url: 'https://git.eigen.co.id',
    icon: '📦',
    description: 'Source code repositories',
    color: '#5E2C8C',
  },
  {
    name: 'Concourse CI',
    url: 'https://cci.eigen.co.id',
    icon: '🔄',
    description: 'CI/CD pipelines & automation',
    color: '#1F7A1F',
  },
  {
    name: 'Kubernetes',
    url: 'https://kubernetes.eigen.co.id',
    icon: '☸️',
    description: 'Cluster management (Headlamp)',
    color: '#326CE5',
  },
  {
    name: 'Grafana',
    url: 'https://grafana.eigen.co.id',
    icon: '📊',
    description: 'Infrastructure monitoring & dashboards',
    color: '#F46800',
  },
  {
    name: 'Traefik',
    url: 'https://traefik.eigen.co.id',
    icon: '🌐',
    description: 'Reverse proxy & ingress dashboard',
    color: '#24B2A0',
  },
  {
    name: 'Harbor',
    url: 'https://harbor.eigen.co.id',
    icon: '🐳',
    description: 'Container image registry',
    color: '#60B044',
  },
  {
    name: 'Keycloak',
    url: 'https://keycloak.eigen.co.id',
    icon: '🔐',
    description: 'Identity & access management',
    color: '#ED7D31',
  },
  {
    name: 'Jira',
    url: 'https://jira.eigen.co.id',
    icon: '🎯',
    description: 'Issue tracking & project management',
    color: '#0052CC',
  },
  {
    name: 'EJBCA',
    url: 'https://pki.eigen.local',
    icon: '🔒',
    description: 'Internal CA & certificate management',
    color: '#8B5CF6',
  },
  {
    name: 'Vault',
    url: 'https://vault.eigen.local',
    icon: '🗝️',
    description: 'Secrets management',
    color: '#FFD700',
  },
  {
    name: 'Huly',
    url: 'https://huly.eigen.co.id',
    icon: '📹',
    description: 'Meetings & online collaboration',
    color: '#6C5CE7',
  },
  {
    name: 'Affine',
    url: 'https://affine.eigen.co.id',
    icon: '📝',
    description: 'Knowledge base & documentation',
    color: '#1E96FC',
  },
  {
    name: 'npm',
    url: 'https://npm.eigen.co.id',
    icon: '📦',
    description: 'Private npm registry (Verdaccio)',
    color: '#CB3837',
  },
  {
    name: 'Mail',
    url: 'https://mail.eigen.co.id',
    icon: '✉️',
    description: 'Webmail (Roundcube)',
    color: '#37BEFF',
  },
];

const bigPictureProjects: BigPictureProject[] = [
  {
    name: 'DevOps',
    url: 'https://jira.eigen.co.id/plugins/servlet/softwareplant-bigpicture/#/box/PROG-14/g',
    icon: '🛠️',
    description: 'Infrastructure & platform roadmap',
    color: '#1F7A1F',
  },
];

const notices = [
  {
    title: '📢 Welcome to Eigen Tech Docs',
    body: 'This is your central hub for Eigen infrastructure, services, and documentation. Use the sidebar to navigate or the shortcuts below to access tools.',
  },
];

export const HomePage = () => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      {/* Welcome Section */}
      <Paper className={classes.welcomeCard} elevation={0}>
        <Typography variant="h4" className={classes.welcomeTitle}>
          Selamat Datang di Eigen Tech Docs
        </Typography>
        <Typography variant="body1" className={classes.welcomeSubtitle}>
          Portal pusat untuk infrastruktur, layanan, dan dokumentasi PT. Eigen Tri Mathema
        </Typography>
        <div className={classes.quickLinks}>
          <Button
            variant="contained"
            color="secondary"
            className={classes.quickLinkBtn}
            component={Link}
            to="/catalog"
          >
            Browse Catalog
          </Button>
          <Button
            variant="outlined"
            className={classes.quickLinkBtn}
            style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
            component={Link}
            to="/docs"
          >
            Documentation
          </Button>
          <Button
            variant="outlined"
            className={classes.quickLinkBtn}
            style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
            component={Link}
            to="/create"
          >
            Create Component
          </Button>
        </div>
      </Paper>

      {/* Notices */}
      {notices.length > 0 && (
        <>
          <Typography variant="h6" className={classes.sectionTitle}>
            📋 Announcements
          </Typography>
          {notices.map((notice, i) => (
            <Paper key={i} className={classes.noticeCard} elevation={1}>
              <Typography className={classes.noticeTitle}>{notice.title}</Typography>
              <Typography className={classes.noticeBody}>{notice.body}</Typography>
            </Paper>
          ))}
        </>
      )}

      <Divider className={classes.divider} />

      {/* Shortcuts */}
      <Typography variant="h6" className={classes.sectionTitle}>
        🔗 Quick Access
      </Typography>
      <Grid container spacing={2} className={classes.shortcutGrid}>
        {shortcuts.map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.shortcutLink}
            >
              <Paper className={classes.shortcutCard} elevation={2}>
                <Typography className={classes.shortcutIcon}>{s.icon}</Typography>
                <Typography className={classes.shortcutName}>{s.name}</Typography>
                <Typography className={classes.shortcutDesc}>{s.description}</Typography>
              </Paper>
            </a>
          </Grid>
        ))}
      </Grid>

      {/* BigPicture Projects */}
      <Divider className={classes.divider} />
      <Typography variant="h6" className={classes.sectionTitle}>
        🗺️ Active BigPicture
      </Typography>
      <Grid container spacing={2} className={classes.shortcutGrid}>
        {bigPictureProjects.map((p, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <a
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.shortcutLink}
            >
              <Paper className={classes.shortcutCard} elevation={2}>
                <Typography className={classes.shortcutIcon}>{p.icon}</Typography>
                <Typography className={classes.shortcutName}>{p.name}</Typography>
                <Typography className={classes.shortcutDesc}>{p.description}</Typography>
              </Paper>
            </a>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};
